import _ from 'lodash';
import { CUSTOM_ELEMENTS_SCHEMA, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';
import { CoreModule } from 'app/core/core.module';
import { MdbAutocompleteModule } from 'mdb-angular-ui-kit/autocomplete';
import { CommonModule } from '@angular/common';
import { GoogleMapsAutocompleteModule } from 'app/google-maps-autocomplete/google-maps-autocomplete.module';
import { NgxCurrencyDirective } from 'ngx-currency';
import { UserFormModalService } from 'app/users/services/user-form-modal/user-form-modal.service';
import { filter, map, pairwise, switchMap } from 'rxjs';
import { UserService } from 'app/users/services/user/user.service';
import { Store, select } from '@ngrx/store';
import { selectCampaignList } from 'app/campaign/features/campaign.selectors';
import { SharedService } from 'app/virtual-event/services/shared.service';
import { loadCampaignList } from 'app/campaign/features/campaign.actions';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { ToastService } from 'app/toast/services/toast.service';
import { ToastModule } from 'app/toast/toast.module';
import { MdbCheckboxModule } from 'mdb-angular-ui-kit/checkbox';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export function minArrayLength(minArrayLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const { length } = control.value;
        return length < minArrayLength ? { minArrayLength: { value: control.value } } : null;
    };
}

@Component({
  standalone:true,
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  imports: [FormsModule,ReactiveFormsModule,MdbFormsModule,MdbSelectModule,MdbCheckboxModule,CoreModule,CommonModule,ToastModule,MdbAutocompleteModule,GoogleMapsAutocompleteModule, NgxCurrencyDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class UserFormComponent implements OnInit {
    destroyRef = inject(DestroyRef)
    public form: FormGroup = new FormGroup({
        name: new FormControl("", [Validators.required]),
        email: new FormControl("", [Validators.required, Validators.pattern(/^([a-zA-Z0-9_\.\-\+])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/)]),
        campaigns: new FormControl([]),
        fullAdmin: new FormControl(false, []),
        campaignManager: new FormControl(false, []),
        eventManager: new FormControl(false, []),
        finance: new FormControl(false, [])
    });

    public campaigns$ = this.store.pipe(
        select(selectCampaignList)
    );

    public hasCampaignManager = false;

    public data: any = null;

    public list: any[] = [{
        index: 1,
        id: "fullAdmin",
        label: "Account Admin",
        description: "An account admin can access everything on this account including all campaigns, events and reports. They can also add other account admins to this account.",
        value: false
    }, {
        index: 2,
        id: "campaignManager",
        label: "Campaign Manager",
        description: "A campaign manager can edit selected campaigns on this account. They can also access the All Donations report and Campaigns report but will only see transactions associated with the selected campaigns.",
        value: false
    }, {
        index: 3,
        id: "eventManager",
        label: "Event Manager",
        description: "An event manager can create a new event, edit and manage existing events, and access all pending carts. They can add pledges, check out carts, check guests in, send out event invitations, event reminders, add guest reminders and pending cart reminders within the event.",
        value: false
    }, {
        index: 4,
        id: "finance",
        label: "Finance",
        description: "A finance manager can access all reports but cannot edit or create new campaigns or events. ",
        value: false
    }];

    constructor(
        private store: Store,
        private sharedService: SharedService,
        private userFormModalService: UserFormModalService,
        private userService: UserService,
        private toastService: ToastService
    ) {
        this.store.dispatch({
            type: loadCampaignList.type,
            payload: this.sharedService.token
        });
    }

    ngOnInit(): void {
        const campaigns = this.form.get('campaigns');

        this.form.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(({ fullAdmin, campaignManager, eventManager, finance }) => {
            const values = fullAdmin ? [fullAdmin, false, false, false]: [fullAdmin, campaignManager, eventManager, finance];
            for(let key in values) {
                const value = values[key];
                const index = parseInt(key);
                this.list[index].value = value;
            }

            if(fullAdmin) {
                this.form.get("campaignManager").setValue(false, { emitEvent: false });
                this.form.get("eventManager").setValue(false, { emitEvent: false });
                this.form.get("finance").setValue(false, { emitEvent: false });

                this.hasCampaignManager = false;
                campaigns.setValue([], { emitEvent: false });

                this.form.get("campaignManager").disable({ emitEvent: false });
                this.form.get("eventManager").disable({ emitEvent: false });
                this.form.get("finance").disable({ emitEvent: false });
            } else {
                this.form.get("campaignManager").enable({ emitEvent: false });
                this.form.get("eventManager").enable({ emitEvent: false });
                this.form.get("finance").enable({ emitEvent: false });
            }
        });
        
        this.form.get('campaignManager').valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((value) => {
            this.hasCampaignManager = value;
            if(!this.hasCampaignManager) {
                campaigns.setValue([]);
            }
        });

        campaigns.valueChanges.pipe(
            filter(list => {
                return list.length < 1 ? false: list[list.length - 1] === "all";
            }),
            switchMap(() => {
                return this.campaigns$;
            }),
            map(campaigns => {
                return campaigns.map(campaign => campaign.id);
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((list) => {
            return campaigns.setValue(["all", ...list], { emitEvent: false });
        });

        campaigns.valueChanges.pipe(
            filter(list => list.length < 2 ? false: list[0] === "all"),
            map(list => list.slice(1, list.length - 1)),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((list) => {
            return campaigns.setValue(list, { emitEvent: false });
        });

        campaigns.valueChanges.pipe(
            pairwise(),
            filter(([previousValue]) => {
                return previousValue.findIndex(element => element === "all") >= 0;
            }),
            filter(([,currentValue]) => {
                return currentValue.findIndex(element => element === "all") < 0;
            }),
            filter(([previousValue, currentValue]) => {
                return previousValue.length - currentValue.length < 2;
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            return campaigns.setValue([], { emitEvent: false });
        });

        if(this.data) {
            this.form.get("name").setValue(this.data.name);
            this.form.get("email").setValue(this.data.email);
            this.form.get("campaigns").setValue(this.data.campaigns);

            const access = this.data.access || [1];

            const isFullAdmin = access.find(value => value === 1);
            if(isFullAdmin) {
                const role = this.list[0];
                role.value = true;
                return this.form.get(role.id).setValue(true);
            }

            for(let index of access) {
                const role = this.list[index-1];
                role.value = true;
                this.form.get(role.id).setValue(true);
            }
        }
    }

    submit() {
        if(this.form.invalid) {
            return this.form.markAllAsTouched();
        };

        const access = this.getAccess();
        const observable = this.data ?
            this.userService.edit(this.data.id, { ...this.form.value, access }):
            this.userService.add({ ...this.form.value, access });
        return observable.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            this.toastService.showSuccess(`User ${this.data ? 'updated': 'added'}`);
            return this.close(true);
        })
    }

    ngAfterViewInit(): void {
    }

    close(message = null) {
        this.userFormModalService.close(message);
    }

    getAccess() {
        const access = [];
        const controls = ['fullAdmin', 'campaignManager', 'eventManager', 'finance'];
        for(let controlName of controls) {
            const { value } = this.form.get(controlName);
            if(value) {
                const element = this.list.find((element) => element.id === controlName);
                access.push(element.index);
            }
        }
        return access;
    }

    isSelected() {
        return this.list.find(element => element.value);
    };
}
